/* eslint-disable react/no-array-index-key */
import Icon from '@ant-design/icons';
import { Button } from 'antd';
import Form from 'antd/es/form';
import { useWatch } from 'antd/es/form/Form';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { KeyboardEventHandler, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useContextReports } from '../../../../../../context/reports';
import { EnumReportTypes, IReportRow } from '../../../../../../types/reports';
import { getInputsCount } from '../../../../../../utils/loan';
import { Plus } from '../../../../../Common/Icon';
import Loading from '../../../../../Common/Loading';
import styles from '../index.module.less';
import ControlsCol from './ControlsCol';
import HeaderCol from './HeaderCol';
import TableCol from './TableCol';
import {
  IReportLoan,
  useReportInterestOnlyToggle,
  useReportLoanAddCashOut,
  useReportLoanAddClosingCosts,
  useReportLoanAddUip,
  useReportLoanAddVaFundingFee,
  useReportLoanRemoveDownPayment,
  useReportRecalculateTax,
} from '../../../../../../hooks/reports';
import { useContextNotifications } from '../../../../../../context/notifications';
import { calculatePrincipalInterest } from '../../../../../../utils/calculatePrincipalInterest';
import { calculateTotal } from '../../../../../../utils/calculateTotal';
import { formatAmount } from '../../../../../../utils/formatAmount';
import { calculateLoanPoints } from '../../../../../../utils/calculateLoanPoints';
import { calculateLoanAmountWithUip } from '../../../../../../utils/calculateLoanAmountWithUip';
import { calculateDownPaymentPrice } from '../../../../../../utils/calculateDownPaymentPrice';
import MismoUpload from '../MismoUpload';
import { calculateLoanToValue } from '../../../../../../utils/calculateLoanToValue';
import { calculateDownPayment } from '../../../../../../utils/calculateDownPayment';
import { calculateDownPaymentPercentage } from '../../../../../../utils/calculateDownPaymentPercentage';

interface IReportForm {
  disabled?: boolean;
  reportType: EnumReportTypes | undefined;
}

const keys: string[] = [];

const ReportForm = ({ disabled, reportType }: IReportForm) => {
  const form = Form.useFormInstance();
  const loans = useWatch('loans', form);
  const { openNotification } = useContextNotifications();
  const { loansLoading, handleLoansUpdate, isBorrower } = useContextReports();

  const typeNewColState = useState<'area' | 'location' | null>(null);
  const [typeNewCol, setTypeNewCol] = typeNewColState;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const toggleRemoveDownPayment = useReportLoanRemoveDownPayment();
  const toggleInterestOnly = useReportInterestOnlyToggle();
  const toggleAddUip = useReportLoanAddUip();
  const toggleAddVaFundingFee = useReportLoanAddVaFundingFee();
  const toggleAddClosingCosts = useReportLoanAddClosingCosts();
  const toggleAddCashOut = useReportLoanAddCashOut();
  const taxRecalculation = useReportRecalculateTax();
  const tableView: IReportRow[] = form.getFieldValue('tableView');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleAddLocation = () => {
    setTypeNewCol('location');
  };

  const handleClose = () => {
    setTypeNewCol(null);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (droppedItem: DropResult) => {
    if (!droppedItem.destination) return;

    const updatedColumns = [...(loans || [])];
    const [reorderedItem] = updatedColumns.splice(droppedItem.source.index, 1);

    updatedColumns.splice(droppedItem.destination.index, 0, reorderedItem);

    form.setFieldValue('loans', updatedColumns);
    handleLoansUpdate?.();
    setIsDragging(false);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (keys.find((key) => key === event.key)) return;
    keys.push(event.key);

    const container = document.getElementById('table-container');
    const inputsCollection = container?.getElementsByTagName('input');

    if (!inputsCollection) return;

    const currentTabIndex = parseInt(document.activeElement?.getAttribute('tabindex') || '', 10);

    const inputs = Array.from(inputsCollection);

    const inputsCount = getInputsCount(tableView);

    if (keys.includes('Control') && keys.includes('ArrowDown')) {
      event.preventDefault();
      const nextInputTabIndex = currentTabIndex + 1;
      const nextInput = inputs.find(
        (input) => parseInt(input.getAttribute('tabindex') || '', 10) === nextInputTabIndex
      );

      nextInput?.focus();
    }

    if (keys.includes('Control') && keys.includes('ArrowUp')) {
      event.preventDefault();
      const nextInputTabIndex = currentTabIndex - 1;

      if (nextInputTabIndex < 0) return;
      const nextInput = inputs.find(
        (input) => parseInt(input.getAttribute('tabindex') || '', 10) === nextInputTabIndex
      );

      nextInput?.focus();
    }

    if (keys.includes('Control') && keys.includes('ArrowRight')) {
      event.preventDefault();
      const nextInputTabIndex = currentTabIndex + inputsCount;
      const nextInput = inputs.find(
        (input) => parseInt(input.getAttribute('tabindex') || '', 10) === nextInputTabIndex
      );

      nextInput?.focus();
    }

    if (keys.includes('Control') && keys.includes('ArrowLeft')) {
      event.preventDefault();
      const nextInputTabIndex = currentTabIndex - inputsCount;

      if (nextInputTabIndex < 0) return;
      const nextInput = inputs.find(
        (input) => parseInt(input.getAttribute('tabindex') || '', 10) === nextInputTabIndex
      );

      nextInput?.focus();
    }
  };

  const handleKeyUp: KeyboardEventHandler<HTMLDivElement> = (event) => {
    const index = keys.findIndex((key) => key === event.key);

    if (index < 0) return;

    keys.splice(index, 1);
  };

  const onCheckboxInterestOnlyToggle = (e: CheckboxChangeEvent, id: string | number) => {
    const isChecked = e.target.checked;

    toggleInterestOnly.fetch({ interestOnly: isChecked }, id);
    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    if (loanIndex !== -1) {
      const updatedLoans = [...loans];
      const currentLoan = updatedLoans[loanIndex];

      const updatedLoan = {
        ...currentLoan,
        interestOnly: isChecked,
      };

      updatedLoan.principalInterest = {
        value: calculatePrincipalInterest(updatedLoan) as string
      }

      const total = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(updatedLoan.principalInterest.value),
        }
      )

      updatedLoan.total = {
        value: total
      }

      const totalWithRateShift= calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(calculatePrincipalInterest(updatedLoan,
            parseFloat(updatedLoan?.interestRate?.value ?? '0') - 0.25
          ) as string),
        }
      )

      updatedLoan.interestChange = {
        value: parseFloat(formatAmount(Math.abs(total - totalWithRateShift)))
      }

      updatedLoans[loanIndex] = updatedLoan;

      form.setFieldValue('loans', updatedLoans);
      handleLoansUpdate?.();
    }
  };

  const onCheckboxRemoveDownPaymentToggle = (e: CheckboxChangeEvent, id: string | number) => {
    const isChecked = e.target.checked;
    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    const downPayment =
      calculateDownPayment(form, loanIndex, { calculateFromLoanAmount: true, removeDownPayment: isChecked });

    const downPaymentPercentage =
      calculateDownPaymentPercentage(form, loanIndex, { downPayment, removeDownPayment: isChecked });

    form.setFieldValue(['loans', loanIndex, 'downPayment', 'value'], downPayment);
    form.setFieldValue(['loans', loanIndex, 'downPaymentPercentage', 'value'], downPaymentPercentage);
    form.setFieldValue(['loans', loanIndex, 'externalLoan', 'removeDownPayment'], isChecked);

    const downPaymentPrice = calculateDownPaymentPrice(form, loanIndex)

    toggleRemoveDownPayment.fetch({
      removeDownPayment: isChecked,
      downPayment,
      downPaymentPercentage,
      downPaymentPrice
    }, id);

    form.setFieldValue(['loans', loanIndex, 'down_payment_price'], downPaymentPrice);
  }

  const onCheckAddUipToggle = (e: CheckboxChangeEvent, id: string | number) => {
    const isChecked = e.target.checked

    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    if (loanIndex !== -1) {
      const updatedLoans = [...loans];
      const currentLoan = { ...updatedLoans[loanIndex] };
      const addVaFundingFee = currentLoan?.externalLoan?.addVaFundingFee;

      const addVaFundingFeeValue = isChecked && addVaFundingFee ? false : addVaFundingFee;

      toggleAddUip.fetch({ addUip: isChecked, addVaFundingFee: addVaFundingFeeValue }, id);

      const uip = parseFloat(currentLoan?.uip?.value ?? 0);
      const vaFundingFee = currentLoan?.externalLoan?.vaFundingFee || 0;
      const loanPointsPercentage = parseFloat(currentLoan?.loanPointsPercentage?.value ?? 0);
      let closingCosts = currentLoan?.closingCosts?.value ?? 0;

      if (isChecked) {
        closingCosts = addVaFundingFee ? (closingCosts - uip) + vaFundingFee : closingCosts - uip;
      } else {
        closingCosts += uip;
      }

      const formatedClosingCosts = parseFloat(formatAmount(closingCosts))

      const loanAmountWithUip = calculateLoanAmountWithUip(
        form,
        loanIndex,
        { closingCosts: formatedClosingCosts, addUip: isChecked, addVaFundingFee: addVaFundingFeeValue }
      )

      const downPaymentPrice = calculateDownPaymentPrice(form, loanIndex, { closingCosts: formatedClosingCosts })

      const loanPoints = calculateLoanPoints(loanPointsPercentage, loanAmountWithUip)

      const loanToValue = calculateLoanToValue(
        form,
        loanIndex,
        { loanAmountWithUip, addUip: isChecked, addVaFundingFee: addVaFundingFeeValue }
      );

      const updatedLoan = {
        ...currentLoan,
        externalLoan: {
          ...currentLoan.externalLoan,
          addVaFundingFee: addVaFundingFeeValue,
          loanToValue,
        },
        addUip: isChecked,
        closingCosts: {
          value: formatedClosingCosts
        },
        loanAmountWithUip: {
          value: loanAmountWithUip,
        },
        loanPoints: {
          value: loanPoints,
        },
        down_payment_price: downPaymentPrice
      };

      updatedLoan.principalInterest.value = calculatePrincipalInterest(updatedLoan)

      const total = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(updatedLoan?.principalInterest?.value ?? '0'),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.total.value = total;

      const principalInterestWithRateShift = calculatePrincipalInterest(
        updatedLoan,
        parseFloat(updatedLoan?.interestRate.value as string) - 0.25
      )

      const formatedPrincipalInterestWithRateShift = formatAmount(
        parseFloat(principalInterestWithRateShift as string ?? '0'))

      const totalWithRateShift = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(formatedPrincipalInterestWithRateShift),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.interestChange.value = formatAmount(Math.abs(total - totalWithRateShift));

      updatedLoans[loanIndex] = updatedLoan;

      form.setFieldValue('loans', updatedLoans);
      handleLoansUpdate?.({}, true);

      // getReportSettings?.()
      // form.setFieldValue(
      //   ['loans', loanIndex, 'loanAmountWithUip'],
      //   Number(loans[loanIndex].loanAmount) + Number(loans[loanIndex].uip)
      // );
    }
  };

  const onCheckAddVaFundingFeeToggle = (e: CheckboxChangeEvent, id: string | number) => {
    const isChecked = e.target.checked

    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    if (loanIndex !== -1) {
      const updatedLoans = [...loans];
      const currentLoan = { ...updatedLoans[loanIndex] };
      const addUip = isChecked && currentLoan?.addUip ? false : currentLoan.addUip;
      const uip = parseFloat(currentLoan?.uip?.value) || 0;

      toggleAddVaFundingFee.fetch({ addVaFundingFee: isChecked, addUip }, id);

      const vaFundingFee = currentLoan?.externalLoan?.vaFundingFee || 0;
      const loanPointsPercentage = parseFloat(currentLoan?.loanPointsPercentage?.value ?? 0);
      let closingCosts = currentLoan?.closingCosts?.value ?? 0;

      if (isChecked) {
        closingCosts = currentLoan?.addUip ? (closingCosts - vaFundingFee) + uip : closingCosts - vaFundingFee;
      } else {
        closingCosts += vaFundingFee;
      }

      const formatedClosingCosts = parseFloat(formatAmount(closingCosts))

      const downPaymentPrice = calculateDownPaymentPrice(form, loanIndex, { closingCosts: formatedClosingCosts })

      const loanAmountWithUip = calculateLoanAmountWithUip(
        form,
        loanIndex,
        { closingCosts: formatedClosingCosts, addVaFundingFee: isChecked, addUip }
      )

      const loanPoints = calculateLoanPoints(loanPointsPercentage, loanAmountWithUip)

      const loanToValue = calculateLoanToValue(
        form,
        loanIndex,
        { loanAmountWithUip, addVaFundingFee: isChecked, addUip }
      );

      const updatedLoan = {
        ...currentLoan,
        addUip,
        closingCosts: {
          value: formatedClosingCosts
        },
        externalLoan: {
          ...currentLoan.externalLoan,
          addVaFundingFee: isChecked,
          loanToValue
        },
        loanAmountWithUip: {
          value: loanAmountWithUip,
        },
        loanPoints: {
          value: loanPoints,
        },
        down_payment_price: downPaymentPrice
      };

      updatedLoan.principalInterest.value = calculatePrincipalInterest(updatedLoan)

      const total = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(updatedLoan?.principalInterest?.value ?? '0'),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.total.value = total;

      const principalInterestWithRateShift = calculatePrincipalInterest(
        updatedLoan,
        parseFloat(updatedLoan?.interestRate.value as string) - 0.25
      )

      const formatedPrincipalInterestWithRateShift = formatAmount(
        parseFloat(principalInterestWithRateShift as string ?? '0'))

      const totalWithRateShift = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(formatedPrincipalInterestWithRateShift),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.interestChange.value = formatAmount(Math.abs(total - totalWithRateShift));

      updatedLoans[loanIndex] = updatedLoan;

      form.setFieldValue('loans', updatedLoans);
      handleLoansUpdate?.({}, true);
    }
  }

  const onCheckAddClosingCostsToggle = (e: CheckboxChangeEvent, id: string | number) => {
    const isChecked = e.target.checked

    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    if (loanIndex !== -1) {
      const updatedLoans = [...loans];
      const currentLoan = { ...updatedLoans[loanIndex] };

      const loanAmountWithUip = calculateLoanAmountWithUip(form, loanIndex, { addClosingCosts: isChecked })

      toggleAddClosingCosts.fetch({ addClosingCosts: isChecked }, id);

      const loanPointsPercentage = parseFloat(currentLoan?.loanPointsPercentage?.value ?? 0);

      const loanPoints = calculateLoanPoints(loanPointsPercentage, loanAmountWithUip)

      const loanToValue = calculateLoanToValue(form, loanIndex, { loanAmountWithUip, addClosingCosts: isChecked });

      const updatedLoan = {
        ...currentLoan,
        externalLoan: {
          ...currentLoan.externalLoan,
          addClosingCosts: isChecked,
          loanToValue
        },
        loanAmountWithUip: {
          value: loanAmountWithUip,
        },
        loanPoints: {
          value: loanPoints,
        }
      };

      updatedLoan.principalInterest.value = calculatePrincipalInterest(updatedLoan)

      const total = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(updatedLoan?.principalInterest?.value ?? '0'),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.total.value = total;

      const principalInterestWithRateShift = calculatePrincipalInterest(
        updatedLoan,
        parseFloat(updatedLoan?.interestRate.value as string) - 0.25
      )

      const formatedPrincipalInterestWithRateShift = formatAmount(
        parseFloat(principalInterestWithRateShift as string ?? '0'))

      const totalWithRateShift = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(formatedPrincipalInterestWithRateShift),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.interestChange.value = formatAmount(Math.abs(total - totalWithRateShift));

      updatedLoans[loanIndex] = updatedLoan;

      form.setFieldValue('loans', updatedLoans);
      handleLoansUpdate?.({}, true);

    }
  }

  const onCheckAddCashOutToggle = (e: CheckboxChangeEvent, id: string | number) => {
    const isChecked = e.target.checked

    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    if (loanIndex !== -1) {
      const updatedLoans = [...loans];
      const currentLoan = { ...updatedLoans[loanIndex] };

      const loanAmountWithUip = calculateLoanAmountWithUip(form, loanIndex, { addCashOut: isChecked });

      toggleAddCashOut.fetch({ addCashOut: isChecked }, id);

      const loanPointsPercentage = parseFloat(currentLoan?.loanPointsPercentage?.value ?? 0);

      const loanPoints = calculateLoanPoints(loanPointsPercentage, loanAmountWithUip)

      const loanToValue = calculateLoanToValue(form, loanIndex, { loanAmountWithUip, addCashOut: isChecked });

      const updatedLoan = {
        ...currentLoan,
        externalLoan: {
          ...currentLoan.externalLoan,
          addCashOut: isChecked,
          loanToValue
        },
        loanAmountWithUip: {
          value: loanAmountWithUip,
        },
        loanPoints: {
          value: loanPoints,
        }
      };

      updatedLoan.principalInterest.value = calculatePrincipalInterest(updatedLoan)

      const total = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(updatedLoan?.principalInterest?.value ?? '0'),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.total.value = total;

      const principalInterestWithRateShift = calculatePrincipalInterest(
        updatedLoan,
        parseFloat(updatedLoan?.interestRate.value as string) - 0.25
      )

      const formatedPrincipalInterestWithRateShift = formatAmount(
        parseFloat(principalInterestWithRateShift as string ?? '0'))

      const totalWithRateShift = calculateTotal(
        form,
        loanIndex,
        {
          principalInterest: parseFloat(formatedPrincipalInterestWithRateShift),
          propertyTaxes: parseFloat(updatedLoan?.propertyTaxes?.value ?? '0'),
          homeInsurance: parseFloat(updatedLoan?.homeInsurance?.value ?? '0'),
          hoaDues: parseFloat(updatedLoan?.hoaDues?.value ?? '0'),
          pmi: parseFloat(updatedLoan?.pmi?.value ?? '0'),
        }
      )

      updatedLoan.interestChange.value = formatAmount(Math.abs(total - totalWithRateShift));

      updatedLoans[loanIndex] = updatedLoan;

      form.setFieldValue('loans', updatedLoans);
      handleLoansUpdate?.({}, true);
    }
  }

  const onCheckboxRecalculate = (e: CheckboxChangeEvent, id: string, zpid: string) => {
    setIsLoading(true);

    const loanIndex = loans?.findIndex((loan: IReportLoan) => loan.id === id);

    if (loanIndex === -1) {
      setIsLoading(false);

      return;
    }
    const updatedLoans = [...loans];

    taxRecalculation
      .fetch({ recalculateTax: e.target.checked, zpid, loanId: id })
      .then((res) => {
        const propertyTaxes = res?.data?.propertyTaxes;

        updatedLoans[loanIndex] = {
          ...updatedLoans[loanIndex],
          recalculateTax: e.target.checked,
          propertyTaxes: {
            value: propertyTaxes,
          },
        };

        form.setFieldValue('loans', updatedLoans);
        handleLoansUpdate?.();
      })
      .catch((error) => {
        openNotification?.({
          message: `Error during Tax calculation: ${error}`,
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="position-relative">
      <Loading visible={!!loansLoading || isLoading} absolute />
      <Form.List name="loans">
        {(fields, { remove }) => (
          <div id="table-container" className={styles.tableRow} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
            <HeaderCol disabled={!!disabled} />
            {/* @ts-ignore this works correctly with these props */}
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
              {/* @ts-ignore this works correctly with these props */}
              <Droppable droppableId="loans" direction="horizontal">
                {(provided) => (
                  // @ts-ignore this works correctly with these props
                  <div className={styles.tableContent} {...provided.droppableProps} ref={provided.innerRef}>
                    {fields.map((field, index) => {
                      if (loans?.[index]?.hidden) {
                        return null;
                      }
                      const loanId: number|undefined = loans?.[index]?.id; // Extract loanId from the loans array
                      const isFirst = index === 0;
                      const isRefinance = reportType === EnumReportTypes.refinance || false;

                      if(isFirst && isRefinance) {
                        return (
                          <TableCol
                          onCheckboxInterestOnlyToggle={onCheckboxInterestOnlyToggle}
                          onCheckboxRemoveDownPaymentToggle={onCheckboxRemoveDownPaymentToggle}
                          onCheckAddUipToggle={onCheckAddUipToggle}
                          onCheckAddVaFundingFeeToggle={onCheckAddVaFundingFeeToggle}
                          onCheckAddClosingCostsToggle={onCheckAddClosingCostsToggle}
                          onCheckAddCashOutToggle={onCheckAddCashOutToggle}
                          onCheckboxRecalculate={onCheckboxRecalculate}
                          key={index}
                          field={field}
                          itemKey={index}
                          isDragging={isDragging}
                          handleRemove={() => {
                            remove(index);
                          }}
                          disabled={!!disabled}
                          isFirst={isFirst}
                          isRefinance={isRefinance}
                          reportType={reportType}
                          loanId={loanId}
                        />
                        )
                      }

                      return (
                        // @ts-ignore this works correctly with these props
                        <Draggable key={index} draggableId={index.toString()} index={index}
                        isDragDisabled={disabled}>
                          {(draggableProvided) => (
                            <TableCol
                              onCheckboxInterestOnlyToggle={onCheckboxInterestOnlyToggle}
                              onCheckboxRemoveDownPaymentToggle={onCheckboxRemoveDownPaymentToggle}
                              onCheckAddUipToggle={onCheckAddUipToggle}
                              onCheckAddVaFundingFeeToggle={onCheckAddVaFundingFeeToggle}
                              onCheckAddClosingCostsToggle={onCheckAddClosingCostsToggle}
                              onCheckAddCashOutToggle={onCheckAddCashOutToggle}
                              onCheckboxRecalculate={onCheckboxRecalculate}
                              key={index}
                              field={field}
                              itemKey={index}
                              isDragging={isDragging}
                              provided={draggableProvided}
                              handleRemove={() => {
                                remove(index);
                              }}
                              disabled={!!disabled}
                              isFirst={isFirst}
                              isRefinance={isRefinance}
                              reportType={reportType}
                              loanId={loanId}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {typeNewCol && !disabled && (
                      <ControlsCol typeNewColState={typeNewColState} handleClose={handleClose} />
                    )}

                    {!disabled && (
                      <div className={styles.tableCol}>
                        <div className={styles.tableHeadCell}>
                          <Button
                            type="ghost"
                            size="small"
                            icon={<Icon component={Plus} />}
                            onClick={handleAddLocation}
                          >
                            add location
                          </Button>
                          {!loans?.length && !typeNewCol && !isBorrower && <MismoUpload />}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </Form.List>
    </div>
  );
};

ReportForm.defaultProps = {
  disabled: false,
};

export default ReportForm;
